import { Grid, GridCol, Group, TextInput } from "@mantine/core"
import { useForm } from "@mantine/form"
import { IconCheck, IconX } from "@tabler/icons-react"
import { Select } from "@tfg/ui"
import { zod4Resolver } from "mantine-form-zod-resolver"
import { useMemo } from "react"
import { z } from "zod"

import {
  filterDeletedAtSelectOptions,
  SelectProps,
} from "~/pages/Admin/Mapping/components/MappingSelect"
import { useCreateMappingElementMutation } from "~/pages/Admin/Mapping/hooks/useCreateMappingElementMutation"
import { showAxiosError } from "~/utils/showAxiosError"

type EditableSelectProps = SelectProps & {
  handleCancel: (id: string) => void
  page: number
}

export function EditableSelect({
  typesList,
  mappingType,
  typeName,
  handleCancel,
  page = 1,
}: EditableSelectProps) {
  const { mutate: createMappingElement } = useCreateMappingElementMutation(
    { page },
    {
      onError: (error) => {
        showAxiosError({
          error: error as Error,
          showNotification: true,
        })
      },
    }
  )

  const form = useForm({
    validate: zod4Resolver(
      z.object({
        alias: z.string().min(2, "Alias is required"),
        typeId: z.string().refine((value) => /^\d+$/.test(value)),
      })
    ),
    initialValues: {
      alias: mappingType.type.alias,
      typeId: mappingType.type.typeId,
    },
  })

  const options = useMemo(
    () => filterDeletedAtSelectOptions(typesList, 0),
    [typesList]
  )

  const handleSubmit = async () => {
    if (form.isValid()) {
      createMappingElement({
        data: {
          alias: form.values.alias,
          typeId: Number(form.values.typeId),
          type: typeName,
        },
      })
      handleCancel(mappingType.type.id)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Grid justify="space-between" w="100%">
        <GridCol span={4}>
          <TextInput
            fw={600}
            placeholder="alias"
            size="sm"
            {...form.getInputProps("alias", { withError: true })}
          />
        </GridCol>
        <GridCol span={8}>
          <Group justify="space-between" w="100%" wrap="nowrap">
            <Select
              placeholder="Select type"
              searchable
              data={options}
              size="sm"
              w="100%"
              {...form.getInputProps("typeId", { withError: true })}
            />
            <IconX
              strokeWidth={3}
              onClick={() => {
                handleCancel(mappingType.type.id)
              }}
            />
            <IconCheck
              strokeWidth={2}
              onClick={handleSubmit}
              color="var(--mantine-color-success)"
            />
          </Group>
        </GridCol>
      </Grid>
    </form>
  )
}
